import { useEffect, useRef, useState } from 'react';
import { MAX_ATTENDEES, MAX_ROOMS, MIN_ATTENDEES, MIN_ROOMS } from '../../constants';
import { RoomsAndEventSpace, Variant } from './RoomsAndEventSpace.types';

const getInitialValue = (value?: RoomsAndEventSpace): RoomsAndEventSpace => ({
  rooms: value?.rooms ?? 0,
  eventAttendees: value?.eventAttendees ?? 0,
});

export const useRoomsAndEventSpace = (
  value: RoomsAndEventSpace | undefined,
  onChange: (data: RoomsAndEventSpace) => void,
  variant: Variant
) => {
  const [roomsAndEventSpace, setRoomsAndEventSpace] = useState<RoomsAndEventSpace>(() => getInitialValue(value));
  const prevVariantRef = useRef<Variant>(variant);

  useEffect(() => {
    if (value?.rooms !== roomsAndEventSpace.rooms || value?.eventAttendees !== roomsAndEventSpace.eventAttendees) {
      setRoomsAndEventSpace(getInitialValue(value));
    }
  }, [value?.rooms, value?.eventAttendees]);

  useEffect(() => {
    if (prevVariantRef.current !== variant) {
      const resetValues = { rooms: 0, eventAttendees: 0 };
      setRoomsAndEventSpace(resetValues);
      onChange(resetValues);
      prevVariantRef.current = variant;
    }
  }, [variant]);

  const increaseRooms = () => {
    setRoomsAndEventSpace(prev => {
      const updatedRooms = { ...prev, rooms: Math.min(MAX_ROOMS, prev.rooms + 1) };
      onChange(updatedRooms);
      return updatedRooms;
    });
  };

  const decreaseRooms = () => {
    setRoomsAndEventSpace(prev => {
      const updatedRooms = { ...prev, rooms: Math.max(MIN_ROOMS, prev.rooms - 1) };
      onChange(updatedRooms);
      return updatedRooms;
    });
  };

  const increaseAttendees = () => {
    setRoomsAndEventSpace(prev => {
      const updatedAttendees = { ...prev, eventAttendees: Math.min(MAX_ATTENDEES, prev.eventAttendees + 1) };
      onChange(updatedAttendees);
      return updatedAttendees;
    });
  };

  const decreaseAttendees = () => {
    setRoomsAndEventSpace(prev => {
      const updatedAttendees = { ...prev, eventAttendees: Math.max(MIN_ATTENDEES, prev.eventAttendees - 1) };
      onChange(updatedAttendees);
      return updatedAttendees;
    });
  };

  const inputRoomHandler = (inputValue: number) => {
    if (inputValue <= MAX_ROOMS && inputValue >= MIN_ROOMS) {
      const updatedRooms = { ...roomsAndEventSpace, rooms: inputValue };
      setRoomsAndEventSpace(updatedRooms);
      onChange(updatedRooms);
    }
  };

  const inputAttendeesHandler = (inputValue: number) => {
    if (inputValue <= MAX_ATTENDEES && inputValue >= MIN_ATTENDEES) {
      const updatedAttendees = { ...roomsAndEventSpace, eventAttendees: inputValue };
      setRoomsAndEventSpace(updatedAttendees);
      onChange(updatedAttendees);
    }
  };

  const handleReset = () => {
    const resetValues = { rooms: 0, eventAttendees: 0 };
    setRoomsAndEventSpace(resetValues);
    onChange(resetValues);
  };

  return {
    roomsAndEventSpace,
    increaseRooms,
    decreaseRooms,
    increaseAttendees,
    decreaseAttendees,
    inputRoomHandler,
    inputAttendeesHandler,
    handleReset,
  };
};
